import { searchStateStore, SearchStateStore } from './searchState';
import { searchSyncStore, SearchSyncStore } from './searchSync';
import { SearchTableBodyType } from '@/features/SearchControl/hooks/useSearchTable/types';
import { baseService } from '@/shared/api/api';
import { RoutesSearch } from '../routes';
import { alertHandler } from '@/shared/utils/alertHandler';
import { ActiveSessionsType, DetailFormType, GroupingTableByIdType, GroupingTableType, JsonReportDataType, JsonTemplateType, NotesModalType, ReportConclusionType, SaveReportType, SearchMNNType, SessionSourceStatusesType, StatusEnum, TemplateType } from '../types';
import { toCamelOrSnakeCase } from '@/shared/lib/toCamelOrSnakeCase';
import { downloadFile } from '@/shared/utils/downloadFile';
import { authStore } from '../../authStore/authStore';
const CLIENT_URL = process.env.NEXT_PUBLIC_CLIENT_URL || '';
class SearchAsync {
  state: SearchStateStore;
  sync: SearchSyncStore;

  constructor(
    storeState: SearchStateStore,
    syncStore: SearchSyncStore,
  ) {
    this.state = storeState;
    this.sync = syncStore;
  }

  async  fetchGetSearchMNN(value: string, label: string, sessionId: number | null) {
    try {
      this.state.loadings.searchMNNLoading = true;
      this.sync.setSearchData([]);
      const params: Record<string, any> = {
        username: authStore.state.userInfo?.username || '',
        mnn_combination: value
      };
      if(sessionId){
        params['session_id'] = sessionId;
      }
      const response = await baseService.post<SearchMNNType[]>(
        RoutesSearch.GetSynonyms,
        { ...params },
      );

      this.sync.setSelectedMNN({ value, label });
      this.sync.setSearchMNN(response.data);

    } catch (error) {
      alertHandler.addAlert({ alert: error });
    } finally {
      this.state.loadings.searchMNNLoading = false;
    }
  }
  async  fetchGetSearchMNNRefresh(sessionId: number) {
    try {
      this.state.loadings.searchMNNLoading = true;
      this.sync.setSearchData([]);
      const response = await baseService.get<SearchMNNType[]>(
        RoutesSearch.GetSessionRefreshMnn,
        {
          params:{
            session_id:sessionId
          }
        },
      );
      this.sync.setSearchMNN(response.data);
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    } finally {
      this.state.loadings.searchMNNLoading = false;
    }
  }

  async  fetchPostRestart(sessionId: number) {
    try {
      this.state.loadings.restart = true;
      this.sync.setSearchData([]);
      await baseService.get(
        `${RoutesSearch.PostRestart}`,
        {
          params:{
            session_id: sessionId
          }
        }
      );
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
      return 500;
    } finally {
      this.state.loadings.restart = false;
    }
  }


  async  fetchPostMnnToSession(id: string) {
    try {
      const mnn = this.state.selectedMNN.value;
      await baseService.post(
        `/sessions/${id}/mnn`,
        {
          mnns: mnn
        },
      );
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async  fetchPostReturnFromBasket(id: number[]) {
    try {
      const sessionId = this.state.activeSession;
      await baseService.post(
        `/patents/restore-documents`,
        {
          document_ids: id,
          session_id: sessionId
        },
      );
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }


  async fetchGetSearchData(mnn: string, selectedSynonyms: string[]) {
    try {
      this.state.loadings.searchDataLoading = true;
    
      const response = await baseService.post<SearchTableBodyType[]>(
        RoutesSearch.PostMNNAndSynonyms,
        {
          username: authStore.state.userInfo?.username || '',
          mnn: mnn,
          synonyms: selectedSynonyms
        },
      );
     
      const newData = response.data.map((item, index) => {
        return {
          ...item, id: index+1
        };
      }); 
      
      const data = toCamelOrSnakeCase<SearchTableBodyType[]>(newData, true);

      this.sync.setSearchData(data);
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    } 
  }

  async fetchGetAutoMNN(mnn: string) {
    try {
      // this.state.loadings.searchAutoLoading = true;
      const response = await baseService.get<string[]>(
        RoutesSearch.GetAutoMNN,
        {
          params: {
            username: authStore.state.userInfo?.username || '',
            mnn: mnn,
          }
        },
      );
      
      this.sync.setSearchAutoMNN(response.data);
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
    finally {
      // this.state.loadings.searchAutoLoading = false;
    }
  }


  async fetchGetSearchBasketData(id?: number) {
    try {
      const sessionId = id ? id : this.state.activeSession;
      const response = await baseService.get<SearchTableBodyType[]>(`sessions/session-removed-patents/${sessionId}`);
     
      const newData = response.data.map((item, index) => {
        return {
          ...item, id: index+1
        };
      }); 
      
      const data = toCamelOrSnakeCase<SearchTableBodyType[]>(newData, true);

      this.sync.setBasketData([...data]);
      return data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
      this.sync.setBasketData([]);
    }
  }

  async fetchGetFiltersTable(session_id: string) {
    try {
      const response = await baseService.get<string[]>(
        `sessions/${session_id}/filters`,
      );
      
      this.sync.setFiltersTable(response.data);
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchGetGroupingTable(session_id: string | number) {
    try {
      const response = await baseService.get<GroupingTableType[]>(
        RoutesSearch.GetGroupingTable,{
          params: {
            session_id: session_id 
          }
        }
      );
      
      this.sync.setGroupingTable(response.data);
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchGetSaveSessionsData(id: number) {
    try {
      this.state.loadings.searchSaveDataLoading = true;
      const response = await baseService.get<{ session_patents: SearchTableBodyType[]; error?: string }>(
        `${RoutesSearch.GetSaveSessionData}/${id}`,
      );
      if(response.data.error){
        alertHandler.addAlert({ status: 'error', defaultText: response.data.error });
      }
      const newData = response.data.session_patents.map((item, index) => {
        return {
          ...item, id: index+1
        };
      }); 
      
      const data = toCamelOrSnakeCase<SearchTableBodyType[]>(newData, true);
      const processedData = data
        .map((item, index) => {
          const timestamp = item.publicationDate 
            ? new Date(item.publicationDate).getTime()
            : Number.MAX_SAFE_INTEGER;
          
          return { item, timestamp, newId: index + 1 };
        })
        .sort((a, b) => b.timestamp - a.timestamp)
        .map(({ item, newId }) => ({ ...item, id: newId }));
      const selectedIds = processedData.filter((item) => item.isResult === true).map(({ id }) => id) || [];
      this.sync.setSearchData(processedData);
      this.sync.setSelectedMnn(selectedIds);
      return processedData;
    } catch (error) {
      this.state.loadings.searchSaveDataLoading = false;
      alertHandler.addAlert({ alert: error });
    }
    finally{
      this.state.loadings.searchSaveDataLoading = false;
    }
  }

  async fetchGetGroupingTableById(id: number) {
    try {
      const response = await baseService.get<GroupingTableByIdType>(
        `patents/${id}/attributes`,
      );
      
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }
  async fetchPostSelectMnnInTableSession(id: number[], flag: boolean) {
    try {
      const sessionId = this.state.activeSession;
      await baseService.post(
        RoutesSearch.PostSaveCheckPatents, {
          session_id: sessionId,
          document_ids: id,
          is_checked: flag,
        }
      );
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
      return 500;
    }
  }

  async fetchGetDetailById(id: number) {
    try {
      const response = await baseService.get<DetailFormType>(
        `patents/${id}/details`,
      );
      
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchGetNotesByRegion(region: string, docNumber: string) {
    try {
      const response = await baseService.get<NotesModalType[]>(
        `patents/${region}/${docNumber}/notes`,
      );
      
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }
  
  async fetchGetActiveSessions() {
    try {
      const response = await baseService.get<ActiveSessionsType[]>(
        RoutesSearch.GetActiveSessions,
      );
      this.sync.setActiveSessions(response.data);
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }

  }


  async fetchGetTemplate() {
    try {
      const response = await baseService.get<TemplateType>(
        RoutesSearch.GetTemplate,
      );
      
      return response.data;
    } catch (error) {
      // alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPostAddCompilation(sessionId: string) {
    try {
      await baseService.post(
        `sessions/${sessionId}/add-compilation-documents`,
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
      return 500;
    }
  }

  async fetchPostUpdateDocs(sessionId: string, isCompilation: boolean) {
    try {
      await baseService.post(
        `sessions/${sessionId}/update-documents`,{
          session_id: sessionId,
          is_compilation_joined: isCompilation
        }
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
      return 500;
    }
  }

  async fetchGetFirstTemplates() {
    try {
      const mnn = this.state.selectedMNN.value;
      const response = await baseService.get<TemplateType[]>(
        `/search-result-template/identification/${mnn}`,
      );
      this.sync.setTemplates(response.data);
      return response.data;
    } catch (error) {
      // alertHandler.addAlert({ alert: error });
    }
  }

  async fetchGetReportForSession(id: number, documentIds: number[]) {
    try {
      const { data } = await baseService.post<ReportConclusionType>(
        `/report/conclusions`,
        {
          session_id: id,
          document_ids: documentIds
        }
      );
      if(data.json_path){
        const jsonData = await this.fetchGetReportFromUrl(data.json_path);
        return { ...data, jsonData };
      }
      return { ...data, jsonData: null };
    } catch (error) {
      // alertHandler.addAlert({ alert: error });
    }
  }

  async fetchGetReportFromUrl(url: string) {
    try {
      const response = await fetch(`${CLIENT_URL}${url}`);
      const data: JsonReportDataType = await response.json();
      return data;
    } catch (error) {
      // alertHandler.addAlert({ alert: error });
    }
  }

  async fetchGetTemplateData(url: string) {
    try {
      const response = await fetch(`${CLIENT_URL}${url}`);
  
      const data: JsonTemplateType = await response.json();
      return data;
    } catch (error) {
      // alertHandler.addAlert({ alert: error });
    }
  }

  async getTemplateByPath(url: string) {
    try {
      const response = await fetch(`${CLIENT_URL}${url}`);
      const data: JsonTemplateType = await response.json();
      return data.template_text;
    } catch (error) {
      // alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPostSaveTemplate(text: string, id: number, title: string) {
    try {
      this.sync.setLoadings(true, 'saveReportLoading');
      const response = await baseService.post(`/search-result-template/update`,
        {
          saved_json_id: id,
          template_title: title,
          template_text: text
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Шаблон был успешно обновлен!' });
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }finally{
      this.sync.setLoadings(false, 'saveReportLoading');
    }
  }

  async fetchPostAddDoc(source: string, name: string) {
    try {
      const sessionId = this.state.activeSession;
      const response = await baseService.post<SearchTableBodyType>(`/patent-search/${sessionId}/manual`,
        {
          number: name,
          source: source
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Документ добавлен!' });
      return toCamelOrSnakeCase<SearchTableBodyType>(response.data, true);
    } catch (error) {
      alertHandler.addAlert({ alert: error });
      return null;
    }
  }


  async fetchPostSaveNewTemplate(mnn: string, title: string, text: string) {
    try {
      this.sync.setLoadings(true, 'saveReportLoading');
      const response = await baseService.post(`/search-result-template/create`,
        {
          title: title,
          combination_mnn: mnn,
          template_text: text
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Шаблон был успешно создан!' });
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }finally{
      this.sync.setLoadings(false, 'saveReportLoading');
    }
  }

  async fetchPatchCanceledSession(id: number, status: keyof typeof StatusEnum, isAlert?: boolean) {
    try {
      await baseService.patch(
        `sessions/${id}/status`,
        {
          status: status
        },
      );
      if(!isAlert){
        alertHandler.addAlert({ status: 'success', defaultText: `Сессия ${StatusEnum[status]}!` });
      }
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPatchCanceledSessionWithName(id: number, status: keyof typeof StatusEnum, isAlert?: boolean) {
    try {
      await baseService.post(`sessions/${id}/cancel`);
      if(!isAlert){
        alertHandler.addAlert({ status: 'success', defaultText: `Сессия ${StatusEnum[status]}!` });
      }
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchSaveMnnPRevWS(id: number, mnnList: SearchMNNType[]) {
    try {
      this.sync.setLoadings(true, 'saveMnn');
      await baseService.post(
        `sessions/save-mnn`,
        {
          mnn_info: mnnList, 
          session_id: id
        },
      );
      return 200;
    } catch (error) {
      this.sync.setLoadings(false, 'saveMnn');
      alertHandler.addAlert({ alert: error });
    } finally {
      this.sync.setLoadings(false, 'saveMnn');
    }
  }



  async fetchPostCreateNewSessions(name?: string) {
    try {
      const response = await baseService.post<{ session_id: number }>(
        `sessions`,
        {
          title: name,
          project_ids: []
        },
      );
      if(name !== undefined) alertHandler.addAlert({ status: 'success', defaultText: 'Создана новая сессия' });
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPostDeletePatents(documentsIds: number[]) {
    try {
      const sessionId = this.state.activeSession;
      const response = await baseService.post<{ session_id: number }>(
        RoutesSearch.PostDeleteDocuments,
        {
          document_ids: documentsIds,
          session_id: sessionId
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Документы удалены!' });
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPostAddQuestions(documentsIds: number[], flag: boolean) {
    try {
      const response = await baseService.post(
        RoutesSearch.PostSetQuestions,
        {
          document_ids: documentsIds,
          question_mark: flag
        },
      );
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }


  async fetchPostNotesByRegion(region: string, docNumber: string, newNotes: string) {
    try {
      await baseService.post(
        `patents/${region}/${docNumber}/notes`,
        {
          note_text: newNotes
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Примечание добавлено!' });
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }
  async fetchPatchNotes(noteId: number, newNotes: string) {
    try {
      await baseService.patch(
        `patents/${noteId}/notes`,
        {
          note_text: newNotes
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Примечание изменено!' });
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchDeleteNotes(noteId: number) {
    try {
      await baseService.delete(
        `patents/${noteId}/notes`,
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Примечание удалено!' });
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPatchGroupingTableById(id:number, ids: number[], expireDate: Date | null, nationalPhaseExpireDate: Date | null, expireDateIfNational: Date | null) {
    try {
      const formatDate = (date: Date | null): string | null => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      await baseService.patch(
        `patents/${id}/attributes`,
        {
          document_id: id,
          selected_table_ids: ids,
          expire_date: formatDate(expireDate),
          national_phase_expire_date: formatDate(nationalPhaseExpireDate),
          expire_date_if_national: formatDate(expireDateIfNational),
        },
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPatchSaveCommentsById(id:number, comment: string) {
    try {
      await baseService.patch(
        `patents/${id}/comment`,
        {
          formula_comment: comment
        },
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPatchDeleteCommentsById(id:number) {
    try {
      await baseService.delete(
        `patents/${id}/comment`,
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Комментарий удален' });
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPatchSaveFormulaById(id:number, formula: string, formulaTranslated: string) {
    try {
      await baseService.patch(
        `patents/${id}/formula`,
        {
          formula: formula,
          translated_formula: formulaTranslated
        },
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPostSaveReport(params: SaveReportType) {
    try {
      const selectedDocumentsNumber = new Set(this.state.selectedMnnInTable);
      const selectedDocumentsId = this.state.searchData.filter(item => selectedDocumentsNumber.has(item.id || 0)).map(item => item.documentId);
      await baseService.post(
        RoutesSearch.PostSaveReport,
        {
          id_session: params.idSession,
          document_ids: [...selectedDocumentsId],
          mnn: params.mnn,
          title: params.title,
          first_paragraph: params.firstParagraph,
          second_paragraph: params.secondParagraph,
          summary_paragraph: params.summaryParagraph,
          summary_table: { ...params.summaryTable },
          note: params.note
        },
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }
  async fetchPostSaveJsonReport(params: SaveReportType) {
    try {
      const selectedDocumentsNumber = new Set(this.state.selectedMnnInTable);
      const selectedDocumentsId = this.state.searchData.filter(item => selectedDocumentsNumber.has(item.id || 0)).map(item => item.documentId);
      await baseService.post(
        RoutesSearch.PostSaveJsonReport,
        {
          id_session: params.idSession,
          document_ids: [...selectedDocumentsId],
          mnn: params.mnn,
          title: params.title,
          first_paragraph: params.firstParagraph,
          second_paragraph: params.secondParagraph,
          summary_paragraph: params.summaryParagraph,
          summary_table: { ...params.summaryTable },
          note: params.note
        },
      );
      
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchPostNewTable(title: string, shortTitle: string, isResultTable?: boolean) {
    try {
      const sessionId = this.state.activeSession;
      await baseService.post(
        'grouping-tables',
        {
          title: title,
          title_short: shortTitle,
          session_id: sessionId,
          is_result_table: isResultTable ? isResultTable : false
        },
      );
      alertHandler.addAlert({ status: 'success', defaultText: 'Создана новая таблица!' });
      this.fetchGetGroupingTable(sessionId || -1);
      return 200;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }
  async fetchGetSearchStatus(id: string) {
    try {
      const response = await baseService.get<SessionSourceStatusesType>(
        RoutesSearch.GetSessionSourceStatuses,
        {
          params: {
            session_id: id
          },
        },
      );
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }
  async fetchGetTemplates(mnn: string) {
    try {
      const response = await baseService.get<TemplateType[]>(`${RoutesSearch.GetTemplates}${mnn}`);
      return response.data;
    } catch (error) {
      alertHandler.addAlert({ alert: error });
    }
  }

  async fetchDownloadDocument(
    params: SaveReportType
  ) {
    try {
      const selectedDocumentsNumber = new Set(this.state.selectedMnnInTable);
      const selectedDocumentsId = this.state.searchData.filter(item => selectedDocumentsNumber.has(item.id || 0)).map(item => item.documentId);
      const { data } = await baseService.post(`/report/download-preview`, 
        {
          id_session: params.idSession,
          mnn: params.mnn,
          title: params.title,
          first_paragraph: params.firstParagraph,
          document_ids: [...selectedDocumentsId],
          second_paragraph: params.secondParagraph,
          summary_paragraph: params.summaryParagraph,
          summary_table: { ...params.summaryTable },
          note: params.note
        },
        {
          responseType: 'blob',
          headers: {
            'Accept-Encoding': 'identity',
          }
        }
      );
      downloadFile(data, 'docx', `${params.title}`);
      return 200;
    }catch (error: any) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          alertHandler.addAlert({
            status: 'error',
            defaultText: json.message,
          });
        } catch {
          console.log(await error.response.data.text());
        }
      } else {
        console.log(error);
      }
    }
  }
}
  

export const searchAsyncStore = new SearchAsync(
  searchStateStore,
  searchSyncStore,
);
export type SearchAsyncStore = typeof searchAsyncStore;
